import React from 'react';
import { Phone, ArrowRight, CheckCircle2, Stamp, Building2, User, FileText, PenTool, Briefcase, UserCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const CarimbosPersonalizados = () => {
  const whatsappNumber = "5585985195369";
  const whatsappMessage = "Olá! Gostaria de solicitar um orçamento para carimbo personalizado.";
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  const scrollToGallery = () => {
    const element = document.getElementById('galeria');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const stampTypes = [
    {
      icon: Building2,
      title: 'Carimbo empresarial',
      description: 'Personalize com logo, razão social e dados da empresa'
    },
    {
      icon: User,
      title: 'Carimbo com nome e profissão',
      description: 'Ideal para profissionais autônomos e liberais'
    },
    {
      icon: FileText,
      title: 'Carimbo com CNPJ',
      description: 'Inclua CNPJ, endereço e informações fiscais'
    },
    {
      icon: PenTool,
      title: 'Carimbo para assinatura',
      description: 'Agilize processos com carimbo de assinatura'
    },
    {
      icon: FileText,
      title: 'Carimbo para documentos',
      description: 'Protocolos, datas, recebimentos e validações'
    },
    {
      icon: UserCircle2,
      title: 'Carimbo personalizado para uso pessoal',
      description: 'Crie seu carimbo exclusivo com arte personalizada'
    },
    {
      icon: Stamp,
      title: 'Refis para carimbos automáticos',
      description: 'Recargas de tinta para todos os modelos de carimbos'
    }
  ];

  const idealFor = [
    { icon: Building2, text: 'Empresas' },
    { icon: Briefcase, text: 'Escritórios' },
    { icon: User, text: 'Profissionais liberais' },
    { icon: UserCircle2, text: 'Médicos' },
    { icon: FileText, text: 'Advogados' },
    { icon: CheckCircle2, text: 'Contadores' },
    { icon: User, text: 'Professores' },
    { icon: Briefcase, text: 'Profissionais autônomos' },
    { icon: UserCircle2, text: 'Uso pessoal' }
  ];

  const howItWorksSteps = [
    {
      number: '01',
      title: 'Envie as informações',
      description: 'Nos informe o que deseja no carimbo'
    },
    {
      number: '02',
      title: 'Arte ou orientação',
      description: 'Envie sua arte ou solicite orientação para a montagem'
    },
    {
      number: '03',
      title: 'Preparação do modelo',
      description: 'A Infortech prepara o modelo para sua aprovação'
    },
    {
      number: '04',
      title: 'Produção',
      description: 'Após aprovação, o carimbo é produzido'
    }
  ];

  return (
    <section id="carimbos" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-2 bg-[#1e3a8a]/10 rounded-full">
            <p className="text-[#1e3a8a] text-sm font-semibold">CARIMBOS PERSONALIZADOS</p>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Carimbos Personalizados em Fortaleza
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Carimbos automáticos personalizados para empresas, profissionais e uso pessoal, com praticidade e excelente acabamento.
          </p>
        </div>

        {/* Área de Destaque com Imagem */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
          {/* Texto */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full mb-6">
              <Stamp className="w-4 h-4 text-[#1e3a8a]" />
              <span className="text-sm font-semibold text-[#1e3a8a]">Carimbo automático personalizado</span>
            </div>
            
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Carimbos feitos sob medida para sua necessidade
            </h3>
            
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Produzimos carimbos automáticos personalizados de acordo com a necessidade do cliente. 
              Ideal para empresas, profissionais, documentos, formulários, recibos e uso pessoal.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => window.open(whatsappLink, '_blank')}
                data-testid="carimbos-whatsapp-btn"
                size="lg"
                className="bg-[#25D366] hover:bg-[#20BD5C] text-white font-bold px-8 py-6 text-base rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200">
                <Phone className="w-5 h-5 mr-2" />
                Solicitar orçamento de carimbo
              </Button>
              
              <Button
                onClick={scrollToGallery}
                data-testid="carimbos-gallery-btn"
                size="lg"
                variant="outline"
                className="border-2 border-slate-300 text-slate-900 hover:bg-slate-50 font-semibold px-8 py-6 text-base rounded-xl transition-all duration-200">
                Ver modelos de carimbos
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>

          {/* Imagem */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-100/50 to-purple-100/30 rounded-3xl blur-2xl"></div>
            <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_50px_rgba(30,58,138,0.15)]">
              <img 
                src="https://images.unsplash.com/photo-1752147392387-466d552179e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxjdXN0b20lMjBzdGFtcCUyMGltcHJlc3Npb24lMjBwYXBlciUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3OTA2ODE4MTZ8MA&ixlib=rb-4.1.0&q=85"
                alt="Carimbo automático personalizado sendo usado em documento"
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>

        {/* Cards de Tipos de Carimbos */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Tipos de Carimbos Disponíveis
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {stampTypes.map((type, index) => {
              const IconComponent = type.icon;
              return (
                <Card 
                  key={index} 
                  className="group hover:shadow-xl transition-all duration-300 border-gray-200 hover:border-[#1e3a8a]/30 hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#1e3a8a]/10 to-[#3b82f6]/10 rounded-lg flex items-center justify-center mb-4 group-hover:from-[#1e3a8a]/20 group-hover:to-[#3b82f6]/20 transition-all">
                      <IconComponent className="w-6 h-6 text-[#1e3a8a]" />
                    </div>
                    <h4 className="text-base font-semibold text-gray-900 mb-2 group-hover:text-[#1e3a8a] transition-colors">
                      {type.title}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {type.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Ideal Para */}
        <div className="mb-20 bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-8">
            Ideal Para
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6">
            {idealFor.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-white border-2 border-blue-200 rounded-2xl flex items-center justify-center mb-3 hover:border-[#1e3a8a] hover:scale-110 transition-all duration-200">
                    <IconComponent className="w-7 h-7 text-[#1e3a8a]" />
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Como Funciona */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Como Funciona
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorksSteps.map((step, index) => (
              <div key={index} className="relative">
                {/* Connector line (hidden on mobile, shown on md+) */}
                {index < howItWorksSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-blue-300 to-transparent"></div>
                )}
                
                <div className="relative bg-white border-2 border-gray-200 rounded-2xl p-6 hover:border-[#1e3a8a] hover:shadow-lg transition-all duration-300">
                  <div className="text-5xl font-bold text-[#1e3a8a]/20 mb-3">{step.number}</div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* SEO Block */}
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-3xl p-8 md:p-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Carimbos personalizados em Fortaleza
          </h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-4">
              A <span className="font-semibold text-[#1e3a8a]">Infortech produz carimbos personalizados automáticos em Fortaleza</span> para 
              empresas, profissionais e uso pessoal. Trabalhamos com <span className="font-semibold">carimbos para documentos</span>, 
              <span className="font-semibold"> carimbos empresariais</span>, <span className="font-semibold">carimbos profissionais</span>, 
              <span className="font-semibold"> carimbos para assinaturas</span> e outras necessidades.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              Seja para <span className="font-semibold">carimbo com CNPJ</span>, <span className="font-semibold">carimbo para empresa</span> ou 
              <span className="font-semibold"> carimbo automático personalizado</span>, entregamos qualidade e acabamento profissional. 
              Atendemos empresas, escritórios, profissionais liberais e autônomos em toda Fortaleza.
            </p>
            <div className="text-center">
              <Button
                onClick={() => window.open(whatsappLink, '_blank')}
                size="lg"
                className="bg-[#25D366] hover:bg-[#20BD5C] text-white font-bold px-8 py-4 text-base rounded-xl shadow-lg hover:shadow-xl transition-all">
                <Phone className="w-5 h-5 mr-2" />
                Solicite seu orçamento pelo WhatsApp
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default CarimbosPersonalizados;
